import { useContext, useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { FaRegFilePdf } from 'react-icons/fa'

import { useBusinessDataQuery } from '@/hooks/business/useBusinessDataQuery'
import { useBusinessPackagesQuery } from '@/hooks/business/useBusinessPackagesQuery'

import useServer from '../../../api/useServerMain'
import AsyncComboBox from '../../../components/forms/AsyncCombobox'
import Input from '../../../components/forms/Input'
import InputColor from '../../../components/forms/InputColor'
import InputDuration from '../../../components/forms/InputDuration'
import Select from '../../../components/forms/Select'
import TextArea from '../../../components/forms/TextArea'
import Toggle from '../../../components/forms/Toggle'
import Button from '../../../components/misc/Button'
import FacebookStyleImages from '../../../components/misc/Images/FacebookStyleImages'
import { SelectInterface } from '../../../interfaces/InterfacesLocal'
import reportDownloadHandler from '../../../reports/helpers/reportDownloadHandler'
import { useAppSelector } from '../../../store/hooks'
import { DetailProductContext } from '../DetailProductContainer'

const Details = () => {
  const { product, updateProduct, updateStockProductState } =
    useContext(DetailProductContext)
  const { business } = useAppSelector((state) => state.init)
  const { businessData, isLoadingBusinessData, isErrorBusinessData } =
    useBusinessDataQuery({})
  const { businessPackages } = useBusinessPackagesQuery({
    params: { businessId: businessData?.id ?? 0 },
    options: {
      enabled:
        !!businessData?.id && !isLoadingBusinessData && !isErrorBusinessData,
    },
  })
  const { productCategories, measures, salesCategories } = useAppSelector(
    (state) => state.nomenclator
  )
  const { control, handleSubmit, watch, reset } = useForm()
  const { allowRoles: verifyRoles, denyRoles } = useServer()

  useEffect(() => {
    if (product) {
      reset({
        name: product.name,
        measure: product.measure,
        duration: product.duration,
        hasDuration: product.hasDuration,
        color: product.color,
        description: product.description,
      })
    }
  }, [product, reset])

  const onSubmit: SubmitHandler<Record<string, string | number | boolean>> = (
    data
  ) => {
    data.hasDurationEdit = true
    if (updateProduct) updateProduct(product?.id, data, updateStockProductState)
  }

  const updateImages = (
    items: Record<string, number>,
    callback: () => void
  ) => {
    callback()
    updateProduct?.(product?.id, items, updateStockProductState)
  }

  // const precision = Number(
  //   business?.configurationsKey.find(
  //     (item) => item.key === 'precission_after_coma'
  //   )?.value
  // )

  //------------------------------------------------------------------------

  const selectSalesCategory: SelectInterface[] = [
    ...salesCategories
      .filter((cat) => cat.isActive)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
      })),
    { id: null, name: 'Sin categoría' },
  ]

  const selectStockCategory: SelectInterface[] = []
  productCategories?.map((cat) =>
    selectStockCategory.push({
      name: cat.name,
      id: cat.id,
    })
  )
  selectStockCategory.push({
    name: 'Sin categoría',
    id: null,
  })

  const selectMeasure: SelectInterface[] = []
  measures.map((item) =>
    selectMeasure.push({
      id: item.code,
      name: item.value,
    })
  )

  //----------------------------------------------------------------------------------------------------

  //Calculate cost by supplies and fixed cost ----------------------------------------------------------------
  // const totalCostCalculated = () => {
  //   return (
  //     ((product?.fixedCosts.reduce(
  //       (total, value) =>
  //         total +
  //         (value.type === 'percent'
  //           ? (value.costAmount / 100) * (product?.netCost ?? 0)
  //           : value.costAmount),
  //       0
  //     ) ?? 0) +
  //       (product?.supplies.reduce(
  //         (total, value) => total + value.quantity * value.supply.averageCost,
  //         0
  //       ) ?? 0) +
  //       (product?.supplies.length === 0 ? (product?.netCost ?? 0) : 0)) /
  //     (product?.performance ?? 1)
  //   )
  // }

  //-------------------------------------------------------------------------------------------

  const adminRole = verifyRoles([
    'ADMIN',
    'MANAGER_COST_PRICES',
    'PRODUCT_PROCESATOR',
  ])

  const hasDuration = watch('hasDuration') ?? product?.hasDuration

  return (
    <div>
      <div className="relative grid h-[34rem] grid-cols-5 gap-3">
        <FacebookStyleImages
          className="col-span-2 flex h-full w-full rounded border border-gray-300"
          disabled={!adminRole}
          previewDefault={product?.images.map((item) => ({
            id: item.id,
            src: item.src,
            hash: item.blurHash,
          }))}
          submit={updateImages}
        />
        <div className="col-span-3 overflow-auto rounded border border-gray-300 p-2 scrollbar-none">
          <form
            className="flex h-full flex-col items-stretch"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-row justify-between">
              <div className="">
                <p className="text-start text-gray-600">
                  {'Código universal: '}
                  <span className="font-semibold">{product?.salesCode}</span>
                </p>
                {product?.externalId && (
                  <p className="text-start text-gray-600">
                    {'Referencia externa: '}
                    <span className="font-semibold">{product?.externalId}</span>
                  </p>
                )}
              </div>
              <Button
                action={() => {
                  if (business)
                    reportDownloadHandler(
                      'Ficha de producto',
                      'product_sheet',
                      business,
                      product
                    )
                }}
                color="white"
                icon={<FaRegFilePdf className="h-5 text-gray-500" />}
                name="Exportar"
                outline
                textColor="gray-500"
              />
            </div>
            <div className="flex-col space-y-1">
              <Input
                control={control}
                defaultValue={product?.name}
                disabled={!adminRole}
                label="Nombre"
                name="name"
                placeholder="Nombre del producto"
                rules={{ required: 'Este campo es requerido' }}
              />

              {[
                'STOCK',
                'MENU',
                'SERVICE',
                'ADDON',
                'COMBO',
                'VARIATION',
              ].includes(product?.type ?? '') && (
                <Select
                  control={control}
                  data={selectSalesCategory}
                  defaultValue={product?.salesCategory?.id}
                  disabled={!adminRole}
                  label="Categoría de Venta"
                  name="salesCategoryId"
                />
              )}

              {[
                'STOCK',
                'RAW',
                'MANUFACTURED',
                'WASTE',
                'ASSET',
                'VARIATION',
              ].includes(product?.type ?? '') && (
                <Select
                  control={control}
                  data={selectStockCategory}
                  defaultValue={product?.productCategory?.id}
                  disabled={!adminRole}
                  label="Categoría de almacén"
                  name="productCategoryId"
                />
              )}

              {(['RAW', 'MANUFACTURED', 'WASTE'].includes(
                product?.type ?? ''
              ) ||
                (product?.type === 'STOCK' && product.saleByWeight)) && (
                <Select
                  control={control}
                  data={selectMeasure}
                  defaultValue={product?.measure}
                  disabled={!adminRole}
                  label="Unidad de medida"
                  name="measure"
                  rules={{ required: 'Este campo es requerido' }}
                />
              )}

              {business?.subscriptionPlan.code !== 'FREE' && (
                <AsyncComboBox
                  control={control}
                  dataQuery={{
                    url: '/administration/supplier',
                    defaultParams: { all_data: true },
                  }}
                  defaultItem={
                    product?.supplier && {
                      id: product.supplier.id,
                      name: product.supplier.name,
                    }
                  }
                  disabled={!adminRole}
                  label="Proveedor"
                  name="supplierId"
                  normalizeData={{ id: 'id', name: 'name' }}
                  nullOpt={{ id: null, name: 'Sin proveedor' }}
                />
              )}

              {product?.type === 'SERVICE' &&
                // business?.configurationsKey.find(
                //   (itm) => itm.key === 'module_booking'
                // )?.value === 'true'
                businessPackages?.find((itm) => itm.code === 'M_BOOKING')
                  ?.isActive && (
                  <article>
                    <div className="flex items-center gap-x-2">
                      <div className="flex-1">
                        <InputDuration
                          control={control}
                          defaultValue={product?.duration}
                          disabled={!hasDuration}
                          label="Duración"
                          name="duration"
                        />
                      </div>
                      <div className="mt-5">
                        <Toggle
                          control={control}
                          defaultValue={product?.hasDuration}
                          name="hasDuration"
                          title="Tiene duración"
                        />
                      </div>
                    </div>
                    <div className="block">
                      <InputColor
                        control={control}
                        defaultValue={product.color}
                        label="Color "
                        name="color"
                      />
                    </div>
                  </article>
                )}

              <TextArea
                control={control}
                defaultValue={product?.description}
                disabled={!adminRole}
                label="Descripción"
                name="description"
                placeholder="Breve descripción del producto"
              />
            </div>
            <div className="absolute -bottom-14 right-0 flex justify-end self-end pt-10">
              {adminRole && denyRoles(['AUDITOR']) && (
                <Button color="slate-600" name="Actualizar" type="submit" />
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default Details
